import { useApp, useTriggerAction, useUpdateWidgetValue, ConditionalRender } from '@libs/core';
import { ActionActionTypeEnum, TWidget } from '@libs/types';
import imageCompression, { Options } from 'browser-image-compression';
import clsx from 'clsx';
import React, { useState } from 'react';
import { SpinnerCircle } from '../../UI/Spinner/SpinnerCircle';
import { TVisualData } from '../../../types/data';
import { TVisualOption } from '../../../types/options';
import { getInlineStyles } from '../../../utils/styles';
import { Modal } from '../../UI/Modal/Modal';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { VisualWidget } from '../VisualWidget';
import styled from './profileImage.module.css';
import { SelectPhotos } from './SelectPhotos';
import { TakePhoto } from './TakePhoto';
import { WebcamCapture } from './WebcamCapture';

type TProfileImageProps<TD, TO> = {
    widget: TWidget<TD, TO>;
};

enum ActionNameProfilePhotoEnum {
    doUpdateProfilePhoto = 'DoUpdateProfilePhoto',
}

const ProfileImage = ({ widget }: TProfileImageProps<TVisualData, TVisualOption>) => {
    const { setPopup, setModal } = useApp();
    const { triggerActionsByNameAndType } = useTriggerAction(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const [inProgress, setInProgress] = useState<boolean>(false);
    const options = widget.options ?? {};

    const handleChange = async (value: string) => {
        updateWidgetValue(value);
        triggerActionsByNameAndType(ActionActionTypeEnum.onClick, ActionNameProfilePhotoEnum.doUpdateProfilePhoto, { value }).catch((e) => {
            console.error(`Widget '${widget.id}':${widget.uid} catch error of fetch data:`, e);
        });
    };

    const compressImage = async (file: File) => {
        setPopup?.(null);

        const options: Options = {
            maxSizeMB: 0.05,
            maxWidthOrHeight: 320,
            useWebWorker: true,
            onProgress: (p) => {
                setInProgress(p !== 100);
            },
        };

        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();
        reader.readAsDataURL(compressedFile);
        reader.onload = async () => {
            await handleChange(reader.result as string);
        };
        reader.onerror = function () {
            console.error('Error reader', reader.error);
        };
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target?.files?.[0];
        if (file) {
            compressImage(file)
                .catch((e) => {
                    console.error('Error reader', e?.error);
                })
                .finally(() => setInProgress(false));
        }
    };

    const handlePhotoTake = () => {
        setPopup?.(null);
        setModal?.(
            <Modal isCanCloseModal={true} onHandleClick={() => setModal(null)}>
                <WebcamCapture
                    handleClick={async (value: string) => {
                        await handleChange(value);
                        setModal?.(null);
                    }}
                    setModal={setModal}
                />
            </Modal>,
        );
    };

    const handleClick = () => {
        setPopup?.(
            <PopupComponent
                withoutSeparator
                internalContent={
                    <>
                        <SelectPhotos label={options.selectPhotoButtonText} iconName={options.selectPhotoButtonIcon} handleChange={handlePhotoChange} />
                        <TakePhoto label={options.takePhotoButtonText} iconName={options.takePhotoButtonIcon} handleClick={handlePhotoTake} />
                    </>
                }
            />,
        );
    };
    return (
        <div data-test={widget.uid + '-profileImage'} className={clsx(styled.profileImageContainer, inProgress && styled.loading)}>
            <ConditionalRender condition={inProgress}>
                <div
                    className={clsx(styled.loader, widget.options.size === 'medium' && styled.medium)}
                    style={getInlineStyles({ margin: widget.options?.margin })}
                >
                    <SpinnerCircle size={'32px'} />
                </div>
            </ConditionalRender>
            <VisualWidget widget={widget} handleClick={handleClick} />
        </div>
    );
};
export { ProfileImage };
